/**
 * React Query hooks for the theme registry/tags (spec 006 D2).
 */

import { useQuery } from '@tanstack/react-query';
import {
  getThemeDetail,
  getThemeRegistry,
  getThemeTags,
  ThemeDetail,
  ThemeRegistryEntry,
  ThemeTagsResponse,
} from '../themeApi';

export function useThemeTags(symbol?: string | null) {
  return useQuery<ThemeTagsResponse>({
    queryKey: ['theme-tags', symbol],
    queryFn: () => getThemeTags(symbol as string),
    enabled: !!symbol,
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

export function useThemeRegistry() {
  return useQuery<ThemeRegistryEntry[]>({
    queryKey: ['theme-registry'],
    queryFn: getThemeRegistry,
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

/**
 * One theme's thesis, fetched only once a reader asks for it.
 *
 * `slug === null` means "no theme is open", which is the normal state of the card:
 * a modal that fetched on mount would pay for every theme a reader never opened.
 * The registry's own staleness applies — a thesis changes when someone edits it,
 * not on a schedule.
 */
export function useThemeDetail(slug: string | null) {
  return useQuery<ThemeDetail>({
    queryKey: ['theme-detail', slug],
    queryFn: () => getThemeDetail(slug as string),
    enabled: !!slug,
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

/** Registry as a slug-keyed Map, ready for translateTheme(). */
export function registryToMap(
  entries: ThemeRegistryEntry[] | undefined
): Map<string, ThemeRegistryEntry> | null {
  if (!entries) return null;
  return new Map(entries.map((e) => [e.slug, e]));
}
